import type { Questionnaire, QuestionnaireResponse } from "fhir/r4";

import { isAssessmentType } from "./assessment";
import { evaluateFhirPathString } from "./fhirpath";
import { DisplayOption, findScoreConfigFor, ScoreConfig, ScoreProgressDirection } from "./score-config";

export type AssessmentScoreTrend = "PROGRESS" | "REGRESS" | "STABLE" | "NOT_AVAILABLE";

export interface ExtractedScore extends SingleScore {
	component?: (Omit<SingleScore, "score"> & { titleDisplay: string; score: string | undefined })[] | undefined;
}

interface SingleScore {
	score: string;
	max: number | null;
	display: string;
}

/**
 * Extracts the total score from a QuestionnaireResponse, using a hardcoded score config keyed by `Questionnaire.name`.
 *
 * The actual numeric extraction is delegated to a FHIRPath expression so the scoring rules stay close to standard FHIR
 * semantics.
 *
 * Returns `undefined` when:
 *
 * - `questionnaire.name` is missing or not a known `AssessmentType`,
 * - No score config is registered for that name,
 * - The FHIRPath expression yields no numeric result.
 */
export const extractScore = (
	questionnaire: Questionnaire,
	response: QuestionnaireResponse,
): ExtractedScore | undefined => {
	if (!questionnaire.name || !isAssessmentType(questionnaire.name)) {
		return undefined;
	}

	const config = findScoreConfigFor(questionnaire.name);
	if (!config) {
		return undefined;
	}

	const score = evaluateFhirPathString(response, config.expression);
	if (score === undefined) {
		return undefined;
	}

	const max = resolveMax(response, config.max);
	const display = computeDisplayAs(config.displayOption, score, max);

	return {
		score,
		max,
		display,
		component: config.component?.map((subScoreConfig) => {
			const score = evaluateFhirPathString(response, subScoreConfig.expression);
			const titleDisplay = evaluateFhirPathString(questionnaire, subScoreConfig.title.expression);
			const max = resolveMax(response, subScoreConfig.max);
			return {
				titleDisplay: titleDisplay ?? "-",
				score: score,
				max,
				display: computeDisplayAs(subScoreConfig.displayOption, score, max),
			};
		}),
	};
};

/**
 * Resolves a `ScoreConfig` max, evaluating it as a FHIRPath expression against `response` when it isn't already a
 * literal.
 *
 * The literal cases are checked one at a time, rather than as a single `max === null || typeof max === "number"`
 * condition, because consumers compile this source with `strictNullChecks` disabled (`tsconfig.base.json`). Under that
 * mode a `=== null` comparison narrows nothing, so combining the two checks with `||` widens the guarded branch back to
 * include the expression object.
 */
const resolveMax = (response: QuestionnaireResponse, max: ScoreConfig["max"]): number | null => {
	if (typeof max === "number") {
		return max;
	}
	if (max === null) {
		return null;
	}

	const resolved = evaluateFhirPathString(response, max.expression);
	const parsed = resolved === undefined ? NaN : Number(resolved);
	return Number.isFinite(parsed) ? parsed : null;
};

const computeDisplayAs = (displayOption: DisplayOption, score: string | undefined, max: number | null): string => {
	switch (displayOption) {
		case "SCORE_ONLY":
			return `${score ?? "-"}`;
		case "SCORE_SLASH_MAX":
			return `${score ?? "-"}/${max ?? "-"}`;
	}
};

const calculateTrendByDirection = (
	progressDirection: ScoreProgressDirection,
	scores: { current: number | undefined; previous: number | undefined },
): AssessmentScoreTrend => {
	if (scores.current === undefined || scores.previous === undefined) {
		return "NOT_AVAILABLE";
	}

	if (scores.current === scores.previous) {
		return "STABLE";
	}

	switch (progressDirection) {
		case "HIGHER_IS_BETTER":
			return scores.current > scores.previous ? "PROGRESS" : "REGRESS";
		case "LOWER_IS_BETTER":
			return scores.current < scores.previous ? "PROGRESS" : "REGRESS";
	}
};

const numberScoresToNumbers = (context: { currentScore: string; previousScore: string }) => {
	const current = Number(context.currentScore);
	const previous = Number(context.previousScore);

	return {
		current: isNaN(current) ? undefined : current,
		previous: isNaN(previous) ? undefined : previous,
	};
};

const stringScoresToNumbers = (context: {
	currentScore: string;
	previousScore: string;
	progress: Extract<ScoreConfig["progress"], { type: "STRING_SCORE" }>;
}) => {
	const current = context.progress.orderedValues.indexOf(context.currentScore);
	const previous = context.progress.orderedValues.indexOf(context.previousScore);

	return {
		current: current === -1 ? undefined : current,
		previous: previous === -1 ? undefined : previous,
	};
};

/**
 * Ranks each score against its own max (e.g. MNA's screening-only max of 14 vs. its full-assessment max of 30) rather
 * than comparing raw magnitude, so a `max` is required alongside each score. `undefined`/`null` — the score wasn't
 * numeric, or its max couldn't be resolved — falls back to {@link AssessmentScoreTrend} "NOT_AVAILABLE" like the other
 * progress types.
 *
 * The rank decides first, and the raw score only breaks a tie within one rank: a move from 27/30 to 25/30 stays inside
 * MNA's "normal nutritional status" rank but is still a regress. Comparing the raw scores is safe there because a rank
 * covers a single max (MNA's rank 3 is screening-only at max 14, its other ranks are full assessments at max 30), and a
 * higher raw score is the better outcome within one rank.
 */
const rankedScoresToNumbers = (context: {
	current: { score: string; max: number | null | undefined };
	previous: { score: string; max: number | null | undefined };
	progress: Extract<ScoreConfig["progress"], { type: "RANKED_SCORE" }>;
}) => {
	const currentScore = Number(context.current.score);
	const previousScore = Number(context.previous.score);

	if (
		!Number.isFinite(currentScore)
		|| !Number.isFinite(previousScore)
		|| context.current.max == null
		|| context.previous.max == null
	) {
		return { current: undefined, previous: undefined };
	}

	const currentRank = context.progress.rank({ score: currentScore, max: context.current.max });
	const previousRank = context.progress.rank({ score: previousScore, max: context.previous.max });

	return currentRank === previousRank
		? { current: currentScore, previous: previousScore }
		: { current: currentRank, previous: previousRank };
};

type RankedScoreProgress = Extract<ScoreConfig["progress"], { type: "RANKED_SCORE" }>;

type CalculateTrendOptions =
	| {
			currentScore: string;
			previousScore: string | undefined;
			progress: RankedScoreProgress;
			currentMax: number | null | undefined;
			previousMax: number | null | undefined;
	  }
	| {
			currentScore: string;
			previousScore: string | undefined;
			progress: Exclude<ScoreConfig["progress"], RankedScoreProgress>;
	  };

const isRankedScoreOptions = (
	options: CalculateTrendOptions,
): options is Extract<CalculateTrendOptions, { progress: RankedScoreProgress }> =>
	options.progress?.type === "RANKED_SCORE";

export const calculateTrend = (options: CalculateTrendOptions): AssessmentScoreTrend => {
	if (options.previousScore === undefined || options.progress === undefined) {
		return "NOT_AVAILABLE";
	}

	if (isRankedScoreOptions(options)) {
		return calculateTrendByDirection(
			"HIGHER_IS_BETTER",
			rankedScoresToNumbers({
				current: { score: options.currentScore, max: options.currentMax },
				previous: { score: options.previousScore, max: options.previousMax },
				progress: options.progress,
			}),
		);
	}

	switch (options.progress.type) {
		case "NUMBER_SCORE":
			return calculateTrendByDirection(
				options.progress.progressDirection,
				numberScoresToNumbers({ currentScore: options.currentScore, previousScore: options.previousScore }),
			);
		case "STRING_SCORE":
			return calculateTrendByDirection(
				options.progress.progressDirection,
				stringScoresToNumbers({
					currentScore: options.currentScore,
					previousScore: options.previousScore,
					progress: options.progress,
				}),
			);
	}
};
